import {
  ChartBarIcon,
  CheckIcon,
  XCircleIcon,
  PencilIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const getProductivityColor = (level) => {
  switch (level) {
    case "Fast":
      return "text-green-600";
    case "Medium":
      return "text-yellow-600";
    case "Slow":
      return "text-red-600";
    case "Manual":
      return "text-[#d73094]"; 
    default:
      return "text-gray-600";
  }
};

const RoomProductivityConfig = ({
  roomProductivity,
  isEditMode,
  productivityError,
  onToggleEditMode,
  onSaveConfig,
  onProductivityUpdate,
  canEdit = true,
}) => {
  const handleEnterEditMode = () => {
    if (canEdit) {
      onToggleEditMode();
    }
  };

  return (
    <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-start gap-3">
          <ChartBarIcon className="w-5 h-5 text-green-500 mt-1.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Room Productivity Configuration
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Adjust productivity levels (Fast/Medium/Slow/Manual) for each room to
              optimize operations.{" "}
              {productivityError && "(Some values loaded from defaults)"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isEditMode ? (
            <>
              <button
                onClick={onSaveConfig}
                disabled={!canEdit}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                  canEdit
                    ? "bg-black text-white hover:bg-gray-900"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
                title={!canEdit ? "You do not have permission to save changes" : undefined}
              >
                <CheckIcon className="w-4 h-4" />
                Save Changes
              </button>
              <button
                onClick={onToggleEditMode}
                className="px-4 py-2 bg-[#cf350d] text-white rounded-lg text-sm font-medium hover:bg-[#b42e0c] transition-all duration-200 flex items-center gap-2"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleEnterEditMode}
              disabled={!canEdit}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                canEdit
                  ? "bg-black text-white hover:bg-gray-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              title={
                !canEdit
                  ? "You do not have permission to edit productivity settings"
                  : "Edit productivity levels"
              }
            >
              {canEdit ? (
                <PencilIcon className="w-4 h-4" />
              ) : (
                <LockClosedIcon className="w-4 h-4" />
              )}
              {canEdit ? "Edit Settings" : "Edit Locked"}
            </button>
          )}
        </div>
      </div>

      {/* Info Notice */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
          <span>Productivity values represent output units per minute</span>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 min-w-max">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <th className="py-4 px-6 text-left font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gradient-to-r from-blue-50 to-indigo-50 z-10">
                  <div className="flex items-center gap-2 min-w-[200px]">
                    <ChartBarIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>Productivity Level</span>
                  </div>
                </th>

                {roomProductivity.map((roomConfig) => (
                  <th
                    key={roomConfig._id || roomConfig.room}
                    className="py-4 px-4 text-center font-semibold text-gray-900 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 min-w-[120px]"
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-bold">
                        {roomConfig.room}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {["Fast", "Medium", "Slow", "Manual"].map((level, levelIndex) => (  
                <tr
                  key={level}
                  className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                    levelIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="py-4 px-6 font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gray-50 z-10">
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          level === "Fast"
                            ? "bg-green-100 text-green-800"
                            : level === "Medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : level === "Slow"
                            ? "bg-red-100 text-red-800"
                            : "bg-[#fcd9f5] text-[#a10878]"  
                        }`}
                      >
                        {level}
                      </span>

                      <span className="text-sm text-gray-600">
                        {level === "Fast"
                          ? "High Output"
                          : level === "Medium"
                          ? "Standard"
                          : level === "Slow"
                          ? "Low Output"
                          : "Manual (Room 1-3, Room 5 Bags, MZ)"}
                      </span>
                    </div>
                  </td>

                  {roomProductivity.map((roomConfig) => {
                    const unitsPerMin = Math.round(
                      roomConfig.productivity[level.toLowerCase()] || 0
                    );

                    const isManual = level === "Manual";
                    const isAllowedRoom = [
                      "Room 1",
                      "Room 2",
                      "Room 3",
                      "Room 5 Bags",
                      "Manual Zone",
                    ].includes(roomConfig.room);

                    return (
                      <td
                        key={`${roomConfig._id || roomConfig.room}-${level}`}
                        className="py-4 px-4 border-b border-gray-200 text-center"
                      >
                        {isEditMode ? (
                          <div className="flex flex-col items-center gap-1">
                            {isManual && !isAllowedRoom ? (
                              <span className="text-xs text-gray-400">N/A</span>
                            ) : (
                              <>
                                <input
                                  type="number"
                                  value={unitsPerMin}
                                  onChange={(e) =>
                                    onProductivityUpdate(
                                      roomConfig.room,
                                      level,
                                      Math.round(parseFloat(e.target.value) || 0)
                                    )
                                  }
                                  disabled={!canEdit || (isManual && !isAllowedRoom)}
                                  className={`w-20 px-3 py-2 text-center border-2 rounded-lg text-sm font-medium transition-all ${
                                    canEdit && (!isManual || isAllowedRoom)
                                      ? "border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                                  }`}
                                  min="0"
                                  placeholder={isManual ? "7" : ""}
                                />
                                <span className="text-xs text-gray-500">
                                  units/min
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`text-lg font-bold ${getProductivityColor(level)}`}
                            >
                              {unitsPerMin}
                            </span>
                            <span className="text-xs text-gray-500">
                              units/min
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4">
        {["Fast", "Medium", "Slow", "Manual"].map((level) => {  
          const levelDescription =
            level === "Fast"
              ? "High Output"
              : level === "Medium"
              ? "Standard"
              : level === "Slow"
              ? "Low Output"
              : "Manual (Room 1-3, Room 5 Bags, Manual Zone)";

          return (
            <div
              key={level}
              className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                      level === "Fast"
                        ? "bg-green-100 text-green-800"
                        : level === "Medium"
                        ? "bg-yellow-100 text-yellow-800"
                        : level === "Slow"
                        ? "bg-red-100 text-red-800"
                        : "bg-[#fcd9f5] text-[#a10878]"  
                    }`}
                  >
                    {level}
                  </span>
                  <span className="text-sm text-gray-600">
                    {levelDescription}
                  </span>
                </div>
                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded flex items-center gap-1">
                  <InformationCircleIcon className="w-3 h-3" />
                  units/min
                </span>
              </div>

              <div className="space-y-3">
                {roomProductivity.map((roomConfig) => {
                  const unitsPerMin = Math.round(
                    roomConfig.productivity[level.toLowerCase()] || 0
                  );
                  const isManual = level === "Manual";
                  const isAllowedRoom = [
                    "Room 1",
                    "Room 2",
                    "Room 3",
                    "Room 5 Bags",
                    "Manual Zone",
                  ].includes(roomConfig.room);

                  return (
                    <div
                      key={roomConfig._id || roomConfig.room}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {roomConfig.room}
                      </span>
                      <div className="text-right">
                        {isEditMode ? (
                          isManual && !isAllowedRoom ? (
                            <span className="text-xs text-gray-400">N/A</span>
                          ) : (
                            <input
                              type="number"
                              value={unitsPerMin}
                              onChange={(e) =>
                                onProductivityUpdate(
                                  roomConfig.room,
                                  level,
                                  Math.round(parseFloat(e.target.value) || 0)
                                )
                              }
                              disabled={!canEdit || (isManual && !isAllowedRoom)}
                              className={`w-16 px-2 py-1 text-center border-2 rounded-lg text-sm font-medium transition-all ${
                                canEdit && (!isManual || isAllowedRoom)
                                  ? "border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                              }`}
                              min="0"
                              placeholder={isManual ? "7" : ""}
                            />
                          )
                        ) : (
                          <span
                            className={`text-sm font-bold ${getProductivityColor(level)}`}
                          >
                            {unitsPerMin}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Mode Notice */}
      {isEditMode && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3 text-sm text-blue-800">
            <InformationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p>
                Click <strong>Save</strong> to apply changes or{" "}
                <strong>Cancel</strong> to discard.
              </p>
              <p className="mt-1 text-blue-700 text-xs">
                Enter whole number units per minute (e.g., 1 = 1 unit per minute).
                <br />
                <strong>Manual</strong> level is editable for Room 1, 2, 3, Room 5 Bags, and Manual Zone.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomProductivityConfig;

